// src/components/travel/busUtils.js

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';

/**
 * Field configuration for bus data entry
 */
export const BUS_FIELDS = {
    operator: {
        name: 'operator',
        label: 'Bus Operator',
        type: 'select',
        required: true,
        options: () => getBusOperatorsList(),
        allowCustom: true,
        placeholder: 'Select or enter bus operator'
    },
    serviceNumber: {
        name: 'serviceNumber',
        label: 'Service Number',
        type: 'text',
        required: true,
        placeholder: 'e.g. X90, 747',
        validation: value => validateServiceNumber(value)
    },
    departureStop: {
        name: 'departureStop',
        label: 'Departure Stop/Station',
        type: 'select',
        required: true,
        options: () => getBusStopsList(),
        allowCustom: true,
        placeholder: 'Enter departure bus stop or station'
    },
    departureCity: {
        name: 'departureCity',
        label: 'Departure City',
        type: 'text',
        required: false,
        placeholder: 'Enter departure city'
    },
    arrivalStop: {
        name: 'arrivalStop',
        label: 'Arrival Stop/Station',
        type: 'select',
        required: true,
        options: () => getBusStopsList(),
        allowCustom: true,
        placeholder: 'Enter arrival bus stop or station'
    },
    arrivalCity: {
        name: 'arrivalCity',
        label: 'Arrival City',
        type: 'text',
        required: false,
        placeholder: 'Enter arrival city'
    },
    departureTime: {
        name: 'departureTime',
        label: 'Departure Time',
        type: 'datetime',
        required: true,
        placeholder: 'Select departure date and time'
    },
    departureTimezone: {
        name: 'departureTimezone',
        label: 'Departure Timezone',
        type: 'timezone',
        required: false
    },
    arrivalTime: {
        name: 'arrivalTime',
        label: 'Arrival Time',
        type: 'datetime',
        required: true,
        placeholder: 'Select arrival date and time'
    },
    arrivalTimezone: {
        name: 'arrivalTimezone',
        label: 'Arrival Timezone',
        type: 'timezone',
        required: false
    },
    platform: {
        name: 'platform',
        label: 'Platform/Gate',
        type: 'text',
        required: false,
        placeholder: 'e.g. G5, 12'
    },
    seat: {
        name: 'seat',
        label: 'Seat',
        type: 'text',
        required: false,
        placeholder: 'e.g. 15D, Window'
    },
    bookingReference: {
        name: 'bookingReference',
        label: 'Booking Reference',
        type: 'text',
        required: false,
        placeholder: 'Booking/Reservation code'
    },
    bookingAgent: {
        name: 'bookingAgent',
        label: 'Booked Through',
        type: 'text',
        required: false,
        placeholder: 'e.g. Greyhound, Megabus, FlixBus'
    },
    ticketNumbers: {
        name: 'ticketNumbers',
        label: 'Ticket Number(s)',
        type: 'array',
        required: false,
        placeholder: 'Add ticket numbers'
    },
    notes: {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        required: false,
        placeholder: 'Any additional information'
    }
};

/**
 * Common bus operators
 */
const BUS_OPERATORS = [
    { code: 'GRHND', name: 'Greyhound' },
    { code: 'MGBUS', name: 'Megabus' },
    { code: 'FLIXB', name: 'FlixBus' },
    { code: 'NTEXP', name: 'National Express (UK)' },
    { code: 'BOLTB', name: 'BoltBus' },
    { code: 'PETER', name: 'Peter Pan' },
    { code: 'EURLN', name: 'Eurolines' },
    { code: 'REDCO', name: 'Redcoach' },
    { code: 'ADRNL', name: 'Adirondack Trailways' },
    { code: 'BADGR', name: 'Badger Bus' },
    { code: 'JEFFL', name: 'Jefferson Lines' },
    { code: 'COACH', name: 'Coach USA' },
    { code: 'TRAIL', name: 'Trailways' },
    { code: 'GREYC', name: 'Greyhound Canada' },
    { code: 'EURED', name: 'Eurobus' },
    { code: 'ALSA', name: 'ALSA (Spain)' },
    { code: 'OUIB', name: 'OUIBUS (France)' },
    { code: 'REGIO', name: 'RegioJet' },
    { code: 'FLIX', name: 'FlixBus' },
    { code: 'ECOAC', name: 'Ecoach' }
];

/**
 * Get the list of bus operators
 * @returns {Array} List of bus operators
 */
export const getBusOperatorsList = () => {
    return BUS_OPERATORS.map(operator => ({
        value: operator.code,
        label: operator.name,
        code: operator.code,
        name: operator.name
    }));
};

/**
 * Validate service number format
 * @param {string} serviceNumber - Service number to validate
 * @returns {Object} Validation result
 */
export const validateServiceNumber = (serviceNumber) => {
    if (!serviceNumber) {
        return { isValid: false, error: 'Service number is required' };
    }
    
    // Bus service numbers can vary widely
    // Accept almost any format, but trim whitespace
    const trimmed = serviceNumber.trim();
    
    if (trimmed.length === 0) {
        return { isValid: false, error: 'Service number cannot be empty' };
    }
    
    return { isValid: true, parsed: trimmed };
};

/**
 * Get list of common bus stations/stops
 * @returns {Array} List of bus stations
 */
export const getBusStopsList = () => {
    return []; // In a real implementation, this would return actual stops
};

/**
 * Hook to fetch and manage bus fields
 */
export const useBusFields = (tripId) => {
    const { currentUser } = useAuth();
    const { effectiveUserId } = useTrip();
    const [busStations, setBusStations] = useState([]);
    const [busOperators, setBusOperators] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBusData = async () => {
            try {
                // This would normally fetch from an API
                setBusOperators(getBusOperatorsList());
                setBusStations(getBusStopsList());
            } catch (error) {
                console.error('Error fetching bus data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBusData();
    }, [tripId, currentUser, effectiveUserId]);

    return {
        busStations,
        busOperators,
        loading
    };
};

/**
 * Get timezone for a city
 */
export const getTimezoneForCity = (cityCode) => {
    // This would be implemented to return actual timezone data
    return null;
};

/**
 * Normalize bus data for storage
 */
export const normalizeBusData = (formData) => {
    const normalized = { ...formData };
    
    // Normalize service number
    if (normalized.serviceNumber) {
        normalized.serviceNumber = normalized.serviceNumber.trim();
    }
    
    // Normalize booking link
    if (normalized.bookingLink && !normalized.bookingLink.startsWith('http')) {
        normalized.bookingLink = `https://${normalized.bookingLink}`;
    }
    
    // Ensure date fields are properly formatted ISO strings
    if (normalized.departureTime instanceof Date) {
        normalized.departureTime = normalized.departureTime.toISOString();
    }
    
    if (normalized.arrivalTime instanceof Date) {
        normalized.arrivalTime = normalized.arrivalTime.toISOString();
    }
    
    // Ensure timezone data is properly formatted
    if (normalized.departureTimezone && typeof normalized.departureTimezone === 'object') {
        normalized.departureTimezone = normalized.departureTimezone.value || normalized.departureTimezone;
    }
    
    if (normalized.arrivalTimezone && typeof normalized.arrivalTimezone === 'object') {
        normalized.arrivalTimezone = normalized.arrivalTimezone.value || normalized.arrivalTimezone;
    }
    
    return normalized;
};

/**
 * Validate bus data before submission
 */
export const validateBusData = (formData) => {
    const errors = {};
    
    // Validate required operator
    if (!formData.operator) {
        errors.operator = 'Bus Operator is required';
    }
    
    // Validate required service number
    if (!formData.serviceNumber) {
        errors.serviceNumber = 'Service Number is required';
    }
    
    // Validate required departure fields
    if (!formData.departureStop) {
        errors.departureStop = 'Departure Stop is required';
    }
    
    // Validate required arrival fields
    if (!formData.arrivalStop) {
        errors.arrivalStop = 'Arrival Stop is required';
    }
    
    // Validate required time fields
    if (!formData.departureTime) {
        errors.departureTime = 'Departure Time is required';
    }
    
    if (!formData.arrivalTime) {
        errors.arrivalTime = 'Arrival Time is required';
    }
    
    // Validate time order if both times are provided
    if (formData.departureTime && formData.arrivalTime) {
        // Parse dates for comparison
        const departureTime = formData.departureTime instanceof Date 
            ? formData.departureTime 
            : new Date(formData.departureTime);
        
        const arrivalTime = formData.arrivalTime instanceof Date 
            ? formData.arrivalTime 
            : new Date(formData.arrivalTime);
        
        // Check if arrival is after departure
        if (departureTime && arrivalTime && departureTime > arrivalTime) {
            errors.time = 'Arrival time cannot be earlier than departure time';
        }
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}; 